import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createRoot } from "react-dom/client";
import PopupContent from "./PopupContent";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Toast } from "../../helpers/alert";
import FilterOptions from "./FilterOptions";
import "./LineItemText.css";
import {
  deleteBill,
  existBill,
  getUploadedBills,
} from "../../redux/apiCalls/ebmData/dataCall";

const ListBills = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.currentUser);
  const companies = useSelector((state) => state.companies.companies);
  const data = useSelector((state) => state.data);
  const navigate = useNavigate();

  const newWindowRef = useRef(null);
  const popupRootRef = useRef(null);

  const [filterOptions, setFilterOptions] = useState({});
  const [firstOpen, setFirstOpen] = useState(true);

  const handleClick = (id, e) => {
    e.preventDefault();
    navigate(`/editBill/${id}`);
  };

  const abrirPopupVacio = () => {
    const opcionesVentana = "width=800,height=500,scrollbars=yes";

    if (newWindowRef.current && !newWindowRef.current.closed) {
      newWindowRef.current.close();
    }

    const popup = window.open("", "_blank", opcionesVentana);
    newWindowRef.current = popup;
    popupRootRef.current = null;

    if (!popup) {
      Toast.fire({
        icon: "error",
        title: "El navegador bloqueó la ventana emergente.",
      });
      return null;
    }

    popup.document.title = "Facturas cargadas";
    popup.document.body.innerHTML = "<p>Cargando facturas...</p>";

    return popup;
  };

  const renderPopupContent = (popup, bills) => {
    if (!popup || popup.closed) {
      Toast.fire({
        icon: "error",
        title: "La ventana emergente fue cerrada.",
      });
      return;
    }

    popup.document.body.innerHTML = "";

    const contenedor = popup.document.createElement("div");
    popup.document.body.appendChild(contenedor);

    popupRootRef.current = createRoot(contenedor);

    popupRootRef.current.render(
      <PopupContent
        bills={bills}
        filterOptions={filterOptions}
        user={user}
        handleClick={handleClick}
        handleDelete={handleDelete}
        companies={companies}
        handleSearch={handleSearch}
        firstOpen={firstOpen}
        setFirstOpen={setFirstOpen}
      />
    );
  };

  const abrirPrimeraVez = async () => {
    setFirstOpen(false);

    if (!filterOptions.startDate || !filterOptions.endDate) {
      Toast.fire({
        icon: "warning",
        title: "Seleccione ambas fechas para filtrar las facturas.",
      });
      return;
    }

    const popup = abrirPopupVacio();
    if (!popup) return;

    try {
      const bills = await getUploadedBills(
        dispatch,
        user?.accessToken,
        filterOptions
      );

      if (bills?.error) {
        popup.document.body.innerHTML =
          "<p>No se pudieron cargar las facturas.</p>";

        Toast.fire({
          icon: "error",
          title: bills?.message || "No se pudieron obtener las facturas.",
        });

        return;
      }

      renderPopupContent(popup, bills || []);
    } catch (error) {
      popup.document.body.innerHTML =
        "<p>No se pudieron cargar las facturas.</p>";

      Toast.fire({
        icon: "error",
        title: "No se pudieron obtener las facturas.",
      });
    }
  };

  async function handleSearch(id) {
    const popup = abrirPopupVacio();
    if (!popup) return;

    try {
      if (id !== "") {
        const res = await existBill(
          dispatch,
          id,
          "listBills",
          user?.accessToken
        );

        if (res?.error) {
          popup.document.body.innerHTML =
            "<p>No se pudo encontrar la factura.</p>";

          Toast.fire({
            icon: "error",
            title: res?.message || "No se pudo encontrar la factura.",
          });

          return;
        }

        renderPopupContent(popup, res || []);
      } else {
        renderPopupContent(popup, data?.uploadedBills || []);
      }
    } catch (error) {
      popup.document.body.innerHTML =
        "<p>No se pudo encontrar la factura.</p>";

      Toast.fire({
        icon: "error",
        title: "No se pudo encontrar la factura.",
      });
    }
  }

  async function handleDelete(e, id) {
    e.preventDefault();

    Swal.fire({
      title:
        "¿Está seguro de que desea eliminar esta factura? Se eliminarán todos los registros relacionados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar factura.",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      const popup = abrirPopupVacio();
      if (!popup) return;

      try {
        const resDelete = await deleteBill(dispatch, id, user?.accessToken);

        if (resDelete?.error) {
          popup.document.body.innerHTML =
            "<p>No se pudo eliminar la factura.</p>";

          Toast.fire({
            icon: "error",
            title: resDelete?.message || "No se pudo eliminar la factura.",
          });

          return;
        }

        const bills = await getUploadedBills(
          dispatch,
          user?.accessToken,
          filterOptions
        );

        if (bills?.error) {
          popup.document.body.innerHTML =
            "<p>La factura se eliminó, pero no se pudo recargar la lista.</p>";

          Toast.fire({
            icon: "error",
            title:
              bills?.message ||
              "La factura se eliminó, pero no se pudo recargar la lista.",
          });

          return;
        }

        renderPopupContent(popup, bills || []);
      } catch (error) {
        popup.document.body.innerHTML =
          "<p>No se pudo eliminar la factura.</p>";

        Toast.fire({
          icon: "error",
          title: "No se pudo eliminar la factura.",
        });
      }
    });
  }

  return (
    <div
      style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
    >
      <FilterOptions
        filterOptions={filterOptions}
        setFilterOptions={setFilterOptions}
        setFirstOpen={setFirstOpen}
      />

      <button style={{ marginLeft: "10px" }} onClick={abrirPrimeraVez}>
        Ver últimas facturas
      </button>
    </div>
  );
};

export default ListBills;